import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { NbButton } from "../components/nb";
import { Crown, CreditCard, Shield, Trash2, LogOut, Receipt } from "lucide-react";

export default function Settings() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    (async () => { try { const { data } = await api.get("/payments/history"); setPayments(data); } catch {} })();
  }, []);

  const cancelSub = async () => {
    try { const { data } = await api.post("/subscription/cancel"); setUser((u) => ({ ...u, ...data })); toast.success("Subscription canceled."); }
    catch { toast.error("Failed to cancel."); }
  };

  const deleteAccount = async () => {
    try { await api.delete("/profile"); toast.success("Account deleted."); await logout(); navigate("/"); }
    catch { toast.error("Failed to delete account."); }
  };

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <h1 className="font-head font-black text-4xl tracking-tighter">Settings</h1>

        <div className="bg-white nb-border nb-shadow p-6">
          <h2 className="font-head font-black text-xl mb-4 flex items-center gap-2"><Shield className="w-5 h-5" /> Account</h2>
          <div className="space-y-2 text-sm font-medium">
            <div className="flex justify-between"><span className="text-gray-500">Name</span><span>{user.name}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Email</span><span>{user.email}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Email verified</span><span>{user.email_verified ? "Yes ✓" : "No"}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Membership</span><span className="font-bold uppercase">{user.membership}</span></div>
          </div>
        </div>

        <div className="bg-white nb-border nb-shadow p-6">
          <h2 className="font-head font-black text-xl mb-4 flex items-center gap-2"><Crown className="w-5 h-5" /> Subscription</h2>
          {user.membership === "premium" ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="font-medium">Premium active until {user.membership_expires ? new Date(user.membership_expires).toLocaleDateString() : "—"}</div>
              <NbButton variant="danger" onClick={cancelSub} data-testid="cancel-sub">Cancel Subscription</NbButton>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="font-medium">You're on the Free plan.</div>
              <NbButton variant="secondary" onClick={() => navigate("/upgrade")} data-testid="settings-upgrade"><Crown className="w-4 h-4" /> Upgrade</NbButton>
            </div>
          )}
        </div>

        <div className="bg-white nb-border nb-shadow p-6">
          <h2 className="font-head font-black text-xl mb-4 flex items-center gap-2"><Receipt className="w-5 h-5" /> Payment History</h2>
          {payments.length === 0 ? <p className="text-gray-500 font-medium">No payments yet.</p> : (
            <div className="space-y-2">
              {payments.map((p) => (
                <div key={p.id} className="flex items-center justify-between nb-border p-3 text-sm" data-testid={`payment-${p.id}`}>
                  <div><div className="font-bold">{p.plan_name}</div><div className="text-gray-500 text-xs">{new Date(p.created_at).toLocaleString()} · ****{p.card_last4}</div></div>
                  <div className="text-right"><div className="font-head font-black">${p.amount}</div><div className="text-xs text-[#10B981] font-bold uppercase">{p.status}</div></div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-[#F3F3F1] nb-border-thick nb-shadow p-6">
          <h2 className="font-head font-black text-xl mb-4 flex items-center gap-2 text-[#EF4444]"><Trash2 className="w-5 h-5" /> Danger Zone</h2>
          <div className="flex flex-wrap gap-3">
            <NbButton variant="outline" onClick={() => { logout(); navigate("/"); }} data-testid="settings-logout"><LogOut className="w-4 h-4" /> Log Out</NbButton>
            {!confirmDelete ? (
              <NbButton variant="danger" onClick={() => setConfirmDelete(true)} data-testid="delete-account-btn"><Trash2 className="w-4 h-4" /> Delete Account</NbButton>
            ) : (
              <div className="flex gap-2 items-center bg-white nb-border p-2">
                <span className="font-bold text-sm">Are you sure?</span>
                <NbButton variant="danger" onClick={deleteAccount} data-testid="confirm-delete" className="text-xs py-2">Yes, delete</NbButton>
                <NbButton variant="outline" onClick={() => setConfirmDelete(false)} className="text-xs py-2">Cancel</NbButton>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
